// src/pages/SepaMandatPage.tsx

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

/**
 * Öffentliche Seite zur bequemen Umstellung auf SEPA-Lastschrift (zweite Mahnstufe).
 *
 * Route: /sepa-mandat/:token
 * Keine Authentifizierung erforderlich, Absicherung ausschließlich über den Link-Token.
 *
 * Flow:
 * 1. Zustand des Angebots über /.netlify/functions/sepa-mandat-access laden
 * 2. Je nach Zustand: bereits erteilt / abgelaufen / ungültig / Formular
 * 3. Formular: IBAN (ggf. vorbefüllt) und Kontoinhaber bestätigen, Mandat erteilen
 * 4. Abschluss über /.netlify/functions/sepa-mandat-einloesen; danach zahlt der
 *    Kunde künftig per SEPA-Lastschrift
 *
 * Optik bewusst am geschützten Downloadbereich (SecureDocumentAccessPage) orientiert.
 *
 * Wichtig: Die Layout-Hilfskomponenten (Page, Card, Heading, SecurityBadges)
 * sind bewusst auf Modulebene definiert und NICHT innerhalb der Seitenkomponente.
 * Andernfalls wuerden sie bei jedem Tastendruck neu erzeugt, React wuerde die
 * Eingabefelder neu einhaengen und der Fokus ginge nach jedem Zeichen verloren.
 */

interface AngebotInfo {
  valid: boolean;
  alreadyGranted: boolean;
  expired: boolean;
  company: string;
  rechnungsnummer: string;
  betrag: number;
  ibanVorbefuellt: string | null;
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
};

// ============================================
// SHARED: Security Trust Badges (Modulebene)
// ============================================
const SecurityBadges: React.FC<{ compact?: boolean }> = ({ compact = false }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    gap: compact ? '12px' : '20px',
    flexWrap: 'wrap',
    marginTop: compact ? '16px' : '24px',
    marginBottom: compact ? '0' : '8px'
  }}>
    <div style={{
      display: 'flex', alignItems: 'center', gap: '6px',
      fontSize: compact ? '11px' : '12px', color: '#059669',
      backgroundColor: '#ecfdf5', padding: compact ? '4px 8px' : '6px 12px',
      borderRadius: '4px', border: '1px solid #a7f3d0'
    }}>
      <svg width={compact ? '12' : '14'} height={compact ? '12' : '14'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
      SSL-verschlüsselt
    </div>
    <div style={{
      display: 'flex', alignItems: 'center', gap: '6px',
      fontSize: compact ? '11px' : '12px', color: '#2563eb',
      backgroundColor: '#eff6ff', padding: compact ? '4px 8px' : '6px 12px',
      borderRadius: '4px', border: '1px solid #bfdbfe'
    }}>
      <svg width={compact ? '12' : '14'} height={compact ? '12' : '14'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
      DSGVO-konform
    </div>
    <div style={{
      display: 'flex', alignItems: 'center', gap: '6px',
      fontSize: compact ? '11px' : '12px', color: '#374151',
      backgroundColor: '#f3f4f6', padding: compact ? '4px 8px' : '6px 12px',
      borderRadius: '4px', border: '1px solid #d1d5db'
    }}>
      <span style={{ fontSize: compact ? '10px' : '12px' }}>🇩🇪</span>
      Serverstandort: Frankfurt am Main
    </div>
  </div>
);

// ============================================
// Layout-Wrapper (Modulebene)
// ============================================
const Page: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    backgroundColor: '#f9fafb',
    display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px'
  }}>
    {children}
    <style>
      {`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}
    </style>
  </div>
);

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    width: '100%', maxWidth: '500px', backgroundColor: 'white',
    borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    padding: '32px', marginTop: '24px'
  }}>
    {children}
  </div>
);

const Heading: React.FC = () => (
  <div style={{
    width: '100%', maxWidth: '500px', textAlign: 'center',
    marginBottom: '24px', marginTop: '40px'
  }}>
    <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#111827', margin: '0 0 8px 0' }}>
      SEPA-Lastschrift einrichten
    </h1>
    <p style={{ fontSize: '14px', color: '#6b7280', margin: '0' }}>
      Bequem und sicher auf Bankeinzug umstellen
    </p>
  </div>
);

export const SepaMandatPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<AngebotInfo | null>(null);

  // Formularfelder
  const [iban, setIban] = useState('');
  const [kontoinhaber, setKontoinhaber] = useState('');
  const [einwilligung, setEinwilligung] = useState(false);

  // Absende-Status
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [alreadyGrantedAfterSubmit, setAlreadyGrantedAfterSubmit] = useState(false);

  useEffect(() => {
    document.title = 'XTAX - SEPA-Lastschrift einrichten';
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!token) {
        setError('Ungültiger Link. Bitte überprüfen Sie den Zugang.');
        setLoading(false);
        return;
      }
      try {
        const response = await fetch(
          `/.netlify/functions/sepa-mandat-access?token=${encodeURIComponent(token)}`
        );
        const result = await response.json();
        if (!response.ok || !result.valid) {
          setError(result.error || 'Ungültiger Link. Bitte überprüfen Sie den Zugang.');
          setLoading(false);
          return;
        }
        setInfo(result as AngebotInfo);
        if (result.ibanVorbefuellt) {
          setIban(result.ibanVorbefuellt);
        }
        setLoading(false);
      } catch (err) {
        console.error('SEPA mandate access error:', err);
        setError('Ein technischer Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    if (!iban.trim()) {
      setSubmitError('Bitte geben Sie Ihre IBAN ein.');
      return;
    }
    if (!einwilligung) {
      setSubmitError('Bitte bestätigen Sie das SEPA-Lastschriftmandat.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch('/.netlify/functions/sepa-mandat-einloesen', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          iban: iban.trim(),
          kontoinhaber: kontoinhaber.trim() || undefined
        })
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        if (result.expired) {
          setInfo(prev => (prev ? { ...prev, expired: true } : prev));
        }
        setSubmitError(result.error || 'Die Umstellung konnte nicht abgeschlossen werden.');
        setSubmitting(false);
        return;
      }

      if (result.alreadyGranted) {
        setAlreadyGrantedAfterSubmit(true);
      }
      setDone(true);
      setSubmitting(false);
    } catch (err) {
      console.error('SEPA mandate redeem error:', err);
      setSubmitError('Ein technischer Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
      setSubmitting(false);
    }
  };

  // ============================================
  // RENDER: Ladezustand
  // ============================================
  if (loading) {
    return (
      <Page>
        <Heading />
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', color: '#6b7280', fontSize: '14px' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            </svg>
            Wird geladen...
          </div>
        </Card>
      </Page>
    );
  }

  // ============================================
  // RENDER: Ungültiger Link
  // ============================================
  if (error || !info) {
    return (
      <Page>
        <Heading />
        <Card>
          <div style={{
            backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px',
            padding: '16px', display: 'flex', alignItems: 'center', gap: '10px'
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <p style={{ margin: 0, fontSize: '14px', color: '#991b1b' }}>
              {error || 'Ungültiger Link.'}
            </p>
          </div>
        </Card>
      </Page>
    );
  }

  // ============================================
  // RENDER: Erfolg (nach Absenden)
  // ============================================
  if (done) {
    return (
      <Page>
        <Heading />
        <Card>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#ecfdf5',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', margin: '0 0 12px 0' }}>
              {alreadyGrantedAfterSubmit ? 'Mandat bereits erteilt' : 'Vielen Dank'}
            </h2>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0', lineHeight: '1.6' }}>
              {alreadyGrantedAfterSubmit
                ? 'Sie haben uns bereits ein SEPA-Mandat erteilt. Künftige Rechnungen ziehen wir automatisch ein. Sie müssen nichts weiter tun.'
                : 'Ihr SEPA-Lastschriftmandat wurde erfolgreich eingerichtet. Künftige Rechnungen ziehen wir automatisch von Ihrem Konto ein. Dieses Fenster können Sie nun schließen.'}
            </p>
          </div>
        </Card>
      </Page>
    );
  }

  // ============================================
  // RENDER: Mandat bereits erteilt (beim Laden erkannt)
  // ============================================
  if (info.alreadyGranted) {
    return (
      <Page>
        <Heading />
        <Card>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#ecfdf5',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', margin: '0 0 12px 0' }}>
              Mandat bereits erteilt
            </h2>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0', lineHeight: '1.6' }}>
              Sie haben uns bereits ein SEPA-Mandat erteilt. Künftige Rechnungen ziehen wir automatisch ein. Sie müssen nichts weiter tun.
            </p>
          </div>
        </Card>
      </Page>
    );
  }

  // ============================================
  // RENDER: Link abgelaufen
  // ============================================
  if (info.expired) {
    return (
      <Page>
        <Heading />
        <Card>
          <div style={{
            backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px',
            padding: '16px', display: 'flex', alignItems: 'flex-start', gap: '10px'
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" style={{ flexShrink: 0, marginTop: '1px' }}>
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <p style={{ margin: 0, fontSize: '14px', color: '#92400e', lineHeight: '1.5' }}>
              Dieser Link ist abgelaufen. Bitte kontaktieren Sie uns, damit wir Ihnen einen neuen Link zusenden können.
            </p>
          </div>
        </Card>
      </Page>
    );
  }

  // ============================================
  // RENDER: Formular
  // ============================================
  return (
    <Page>
      <Heading />
      <SecurityBadges />
      <Card>
        {/* Info-Box mit offener Rechnung */}
        <div style={{
          backgroundColor: '#f0f7ff', border: '1px solid #bfdbfe', borderRadius: '8px',
          padding: '16px', marginBottom: '24px'
        }}>
          <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: '#1e40af', fontSize: '15px' }}>
            {info.company}
          </p>
          <p style={{ margin: '0', fontSize: '14px', color: '#374151', lineHeight: '1.6' }}>
            Zu Rechnung <strong>{info.rechnungsnummer}</strong> ist derzeit ein Betrag von{' '}
            <strong>{formatCurrency(info.betrag)}</strong> offen. Stellen Sie hier bequem auf
            SEPA-Lastschrift um, damit wir künftige Rechnungen automatisch einziehen und ein
            Zahlungsverzug sich nicht wiederholt.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* IBAN */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
              IBAN
            </label>
            <input
              type="text"
              value={iban}
              onChange={(e) => setIban(e.target.value)}
              placeholder="DE00 0000 0000 0000 0000 00"
              disabled={submitting}
              autoComplete="off"
              style={{
                width: '100%', padding: '14px', fontSize: '16px', border: '1px solid #d1d5db',
                borderRadius: '8px', outline: 'none', boxSizing: 'border-box',
                transition: 'border-color 0.2s, box-shadow 0.2s'
              }}
              onFocus={(e) => { e.target.style.borderColor = '#3465de'; e.target.style.boxShadow = '0 0 0 3px rgba(52, 101, 222, 0.1)'; }}
              onBlur={(e) => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
            />
          </div>

          {/* Kontoinhaber */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
              Kontoinhaber <span style={{ color: '#9ca3af', fontWeight: '400' }}>(optional)</span>
            </label>
            <input
              type="text"
              value={kontoinhaber}
              onChange={(e) => setKontoinhaber(e.target.value)}
              placeholder={info.company}
              disabled={submitting}
              autoComplete="off"
              style={{
                width: '100%', padding: '14px', fontSize: '16px', border: '1px solid #d1d5db',
                borderRadius: '8px', outline: 'none', boxSizing: 'border-box',
                transition: 'border-color 0.2s, box-shadow 0.2s'
              }}
              onFocus={(e) => { e.target.style.borderColor = '#3465de'; e.target.style.boxShadow = '0 0 0 3px rgba(52, 101, 222, 0.1)'; }}
              onBlur={(e) => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
            />
          </div>

          {/* Mandatstext + Einwilligung */}
          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '20px',
            fontSize: '13px', color: '#374151', lineHeight: '1.5', cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={einwilligung}
              onChange={(e) => setEinwilligung(e.target.checked)}
              disabled={submitting}
              style={{ marginTop: '3px', flexShrink: 0, width: '16px', height: '16px', cursor: 'pointer' }}
            />
            <span>
              Ich ermächtige die XTAX GmbH &amp; Co. KG, Zahlungen von meinem Konto mittels
              SEPA-Lastschrift einzuziehen. Zugleich weise ich mein Kreditinstitut an, die von
              der XTAX GmbH &amp; Co. KG auf mein Konto gezogenen Lastschriften einzulösen.
            </span>
          </label>

          {/* Fehlermeldung */}
          {submitError && (
            <div style={{
              backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px',
              padding: '12px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px'
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <p style={{ margin: '0', fontSize: '14px', color: '#991b1b' }}>{submitError}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%', padding: '14px', backgroundColor: submitting ? '#9ca3af' : '#3465de',
              color: 'white', fontSize: '16px', fontWeight: '600', border: 'none', borderRadius: '8px',
              cursor: submitting ? 'not-allowed' : 'pointer', transition: 'background-color 0.2s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
            }}
            onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.backgroundColor = '#2952b3'; }}
            onMouseLeave={(e) => { if (!submitting) e.currentTarget.style.backgroundColor = '#3465de'; }}
          >
            {submitting ? (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                Wird eingerichtet...
              </>
            ) : (
              'Auf Bankeinzug umstellen'
            )}
          </button>
        </form>
      </Card>
    </Page>
  );
};
